/**
 * ContainerGraphView is a component that displays a graphical view of sensor data over time.
 * It allows users to select a time range and specific sensors to filter the data displayed in the graph.
 * Users can also download a screenshot of the graph.
 *
 * Props:
 * - devices: Array of device objects.
 * - sensorsForSelectionInGraphView: Array of sensor objects available for selection.
 * - graphData: Array of data points to be displayed in the graph.
 */

import React, { useEffect, useState, useRef } from "react";
import { Accordion, Button, FormSelect } from "react-bootstrap";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import {
  BulbOutlined,
  CameraOutlined,
  CheckOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useScreenWidth } from "../../../../hooks/useScreenWidth";
import DeviceChartForContainerView from "./DeviceChartForContainerView";
import CustomTimeRangePicker from "../../../Shared/CustomTimeRangePicker/CustomTimeRangePicker";
import TimeFrameNotification from "../../../Shared/TimeFrameNotification/TimeFrameNotification";
import useTranslation from "../../../../hooks/useTranslation";
import { useHandleDeviceLinkClick } from "../../../../utils/navigationUtils";
import "../ContainerMoreInfoPage.scss";
import { getSensorDataByContainerIdWithOptions } from "../../../../api/sensorApi";
import LoadingAnimation from "../../../Shared/LoadingAnimation/LoadingAnimation";
import NoDataForChart from "../../../Shared/Chart/NoDataForChart";

const ContainerGraphView = ({
  containerData,
  devices,
  sensorsForSelectionInGraphView,
  isLoadingGraphData,
  setIsLoadingGraphData,
}) => {
  const { t } = useTranslation("locationsPage");
  const screenWidth = useScreenWidth();
  const handleDeviceLinkClick = useHandleDeviceLinkClick();
  const [selectedSensors, setSelectedSensors] = useState(new Set());
  /*   const [filteredGraphData, setFilteredGraphData] = useState([]); */
  const [timeFormat, setTimeFormat] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  // date picker related variables
  const [showDatePicker, setShowDatePicker] = useState(false);
  // Define state variables for start and end dates
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startDateForGraphTitle, setStartDateForGraphTitle] = useState(null);
  const [endDateForGraphTitle, setEndDateForGraphTitle] = useState(null);
  const chartContainerRef = useRef(null);
  const [graphData, setGraphData] = useState([]); // Move this state here
  const [isDataProcessing, setIsDataProcessing] = useState(false);

  useEffect(() => {
    if (timeFormat === "custom") {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
    }
  }, [timeFormat]);

  // Functions to calculate start dates for different time ranges
  const getLast24hDate = () => {
    const now = new Date();
    return new Date(now - 24 * 60 * 60 * 1000); // 24 hours ago from the adjusted time
  };
  const getLastWeekStartDate = () => {
    const now = new Date();
    return new Date(now - 7 * 24 * 60 * 60 * 1000); // 7 days ago
  };

  const getLastMonthStartDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()); // 30 days ago
  };

  // Callback functions to update date state in parent for custom date range picker
  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Function to handle sensor selection/deselection
  const handleSensorSelection = (deviceId, sensorId, isChecked) => {
    setSelectedSensors((prevSelectedSensors) => {
      const newSelectedSensors = new Set(prevSelectedSensors);
      if (isChecked) {
        newSelectedSensors.add(sensorId);
      } else {
        newSelectedSensors.delete(sensorId);
      }
      return newSelectedSensors;
    });
  };

  // Function to select all sensors
  const handleSelectAllSensors = () => {
    const allSensorIds = sensorsForSelectionInGraphView.map(
      (sensor) => sensor.sensor_id
    );
    setSelectedSensors(new Set(allSensorIds));
  };

  // Function to deselect all sensors
  const handleDeselectAllSensors = () => {
    setSelectedSensors(new Set());
  };

  // Function to submit selected sensors and time format
  const handleSubmitButtonClicked = async () => {
    // Validate that a time format and at least one sensor is selected
    if (timeFormat === "") {
      toast.error(t("select_timeframe"));
      return;
    }

    if (selectedSensors.size === 0) {
      toast.error(t("sensors_not_selected"));
      return;
    }

    try {
      console.time("Total graph data fetch and process");
      setIsLoadingGraphData(true);

      // Determine date range based on selected time format
      let startDateForFilter;
      let endDateForFilter = new Date();
      // Log request parameters

      console.time("API request");
      switch (timeFormat) {
        case "last24h":
          startDateForFilter = getLast24hDate();
          setStartDateForGraphTitle(new Date(Date.now() - 24 * 60 * 60 * 1000));
          break;
        case "lastweek":
          startDateForFilter = getLastWeekStartDate();
          setStartDateForGraphTitle(
            new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
          );
          break;
        case "lastmonth":
          startDateForFilter = getLastMonthStartDate();
          setStartDateForGraphTitle(
            new Date(new Date().setMonth(new Date().getMonth() - 1))
          );
          break;
        case "custom":
          if (startDate && endDate) {
            startDateForFilter = startDate;
            endDateForFilter = endDate;
            setStartDateForGraphTitle(startDate);
            setEndDateForGraphTitle(endDate);
          } else {
            toast.error(t("select_custom_dates"));
            return;
          }
          break;
        default:
          startDateForFilter = null;
      }

      setEndDateForGraphTitle(endDateForFilter);
      console.log(
        `Requesting data for ${selectedSensors.size} sensors from ${startDateForFilter} to ${endDateForFilter}`
      );
      // Convert Set to Array for API call
      const sensorIdsArray = Array.from(selectedSensors);

      // Call API directly
      const data = await getSensorDataByContainerIdWithOptions(
        containerData._id,
        {
          startDate: startDateForFilter,
          endDate: endDateForFilter,
          sensorIds: sensorIdsArray,
        }
      );

      console.timeEnd("API request");
      console.log(`Received ${data.length} data points`);

      // Process timing
      console.time("Data processing");
      // Set data processing flag before formatting data
      setIsDataProcessing(true);

      // API loading is done, but chart rendering is not
      setIsLoadingGraphData(false);

      // Process data
      const formattedData = data.map((sensor) => ({
        sensor_id: sensor.sensor_id,
        probeid: sensor.probeid,
        sens_num: sensor.sens_num,
        sens_value: sensor.sens_value,
        sens_timestamp: sensor.sens_timestamp,
        sens_type: sensor.sens_type
      }));

      console.timeEnd("Data processing");
      // Rendering timing
      console.time("Render preparation");
      setGraphData(formattedData);
      setIsSubmitted(true);
      console.timeEnd("Render preparation");
      console.timeEnd("Total graph data fetch and process");

      if (data.length === 0) {
        toast.info(t("no_data_for_selected_timeframe"));
        setIsDataProcessing(false);
        setIsLoadingGraphData(false);
        setIsSubmitted(true); // Still mark as submitted to show empty state
        setGraphData([]);
        return; // Exit early
      }

      // Calculate dynamic timeout based on data size
      // Baseline: 500ms + additional time per data point
      /*       const dataPoints = formattedData.length;
      const uniqueSensors = new Set(formattedData.map((item) => item.sensor_id))
        .size;
      const timeRange =
        (endDateForFilter - startDateForFilter) / (1000 * 60 * 60 * 24); // in days

      // Formula: base time + time per sensor + time per day in range + time per data point
      const renderTimeout = Math.min(
        500 + uniqueSensors * 100 + timeRange * 50 + dataPoints * 0.5,
        15000 // Cap at 15 seconds max
      );

      console.log(
        `Dynamic render timeout: ${renderTimeout}ms for ${dataPoints} data points, ${uniqueSensors} sensors, ${timeRange.toFixed(
          1
        )} days`
      );

      // Use dynamic timeout
      setTimeout(() => {
   
      }, renderTimeout); */ setIsDataProcessing(false);
    } catch (error) {
      console.error("Error fetching graph data:", error);
      toast.error(t("error_fetching_graph_data"));
      setIsDataProcessing(false);
      setIsLoadingGraphData(false);
    }
  };

  // Group sensors by probeid for display in accordion
  const groupedSensors = sensorsForSelectionInGraphView.reduce(
    (acc, sensor) => {
      (acc[sensor.probeid] = acc[sensor.probeid] || []).push(sensor);
      return acc;
    },
    {}
  );

  // Group sensors by probeid and sort them
  Object.keys(groupedSensors).forEach((probeid) => {
    groupedSensors[probeid].sort((a, b) => a.sens_num - b.sens_num);
  });

  // Take a screenshot of the chart and download it as an image
  const takeScreenshotAndDownload = () => {
    if (chartContainerRef.current) {
      html2canvas(chartContainerRef.current).then((canvas) => {
        const image = canvas
          .toDataURL("image/png")
          .replace("image/png", "image/octet-stream");
        const link = document.createElement("a");
        link.download = "chart-screenshot.png";
        link.href = image;
        link.click();
      });
    } else {
      console.error("Chart container not found");
      toast.error(t("creating_graph_error"));
    }
  };

  useEffect(() => {
    if (graphData.length > 0) {
      console.log("graphData updated:", graphData.length);
    }
  }, [graphData]);

  return (
    <div className="container-graph-view">
      {/* Time range selection */}
      <div className="cgw__filter__container">
        <FormSelect
          id="timeRangeSelect"
          className="cgw__filter__select"
          aria-label="timeRangeSelect"
          onChange={(e) => setTimeFormat(e.target.value)}
        >
          <option value="">{t("select_timeframe")}...</option>
          <option value="last24h">{t("last24h")}</option>
          <option value="lastweek">{t("lastweek")}</option>
          <option value="lastmonth">{t("lastmonth")}</option>
          <option value="custom">{t("customTimeFrame")}</option>
        </FormSelect>
        {showDatePicker && (
          <CustomTimeRangePicker
            show={showDatePicker}
            setShow={setShowDatePicker}
            onDateChange={handleDateChange}
          />
        )}
        {/* Sensor selection accordion */}
        <Accordion className="cgw-accordion">
          <Accordion.Item eventKey="0">
            <Accordion.Header>{t("select_sensors")}</Accordion.Header>
            <Accordion.Body>
              {/* Sensor selection and submit buttons */}
              <div className="cgw__filter__buttons_wrapper">
                <div className="cgw__filter__select-buttons__wrapper">
                  {/* Select/Deselect all sensors buttons, content is based on screen width */}
                  {screenWidth > 768 ? (
                    <Button
                      className="cgw-select-button"
                      onClick={handleSelectAllSensors}
                    >
                      {t("selectAll")}
                    </Button>
                  ) : (
                    <Button
                      className="cgw-select-button"
                      onClick={handleSelectAllSensors}
                    >
                      <CheckOutlined />
                    </Button>
                  )}
                  {screenWidth > 768 ? (
                    <Button
                      className="cgw-select-button"
                      onClick={handleDeselectAllSensors}
                    >
                      {t("unselectAll")}
                    </Button>
                  ) : (
                    <Button
                      className="cgw-select-button"
                      onClick={handleDeselectAllSensors}
                    >
                      <DeleteOutlined />
                    </Button>
                  )}
                </div>
              </div>
              {/* List of devices and sensors */}
              <div className="cgw-devices-list">
                {Object.entries(groupedSensors).map(
                  ([probeid, sensors], index) => (
                    <div key={index} className="cgw-device-card">
                      <h3
                        onClick={(e) =>
                          handleDeviceLinkClick({ probeid, sensors }, e)
                        }
                        className="cgw-device-card__header"
                      >
                        {probeid}
                      </h3>
                      <ul>
                        {sensors.map((sensor) => (
                          <li key={sensor.sensor_id}>
                            <label>
                              <input
                                type="checkbox"
                                name={`sensor-${probeid}`}
                                className="checkbox-round"
                                value={sensor.sensor_id}
                                checked={selectedSensors.has(sensor.sensor_id)} // Ensure checkbox reflects selection state
                                onChange={(e) =>
                                  handleSensorSelection(
                                    sensor.deviceId,
                                    sensor.sensor_id,
                                    e.target.checked
                                  )
                                }
                              />
                              {sensor.sens_num}
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                )}
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
      {/* Submit and screenshot buttons */}
      <div className="cwg-submit-button__wrapper">
        <Button onClick={takeScreenshotAndDownload} className="camera-btn">
          <CameraOutlined />
        </Button>
        {/* Screenshot and submit buttons */}
        <Button
          variant="primary"
          className=" btn cgw-submit-btn"
          onClick={handleSubmitButtonClicked}
        >
          {t("make_graph")}
        </Button>
      </div>
      {/* Chart rendering based on submission status */}
      {/* Show timeframenotification based on timeformat*/}
      {(timeFormat === "lastmonth" || timeFormat === "custom") &&
        !isSubmitted && <TimeFrameNotification />}{" "}
      {/* Chart rendering with two-phase loading */}
      {isLoadingGraphData || isDataProcessing ? (
        // Show loading animation during API call AND data processing
        <div className="cgw-chart-loader-div">
          <LoadingAnimation />
          <p>{isDataProcessing ? t("preparing_chart") : t("loading_data")}</p>
        </div>
      ) : isSubmitted ? (
        // Add check for empty data
        graphData.length > 0 ? (
          // Show chart when there is data
          <div ref={chartContainerRef} className="cgw-chart__container">
            <DeviceChartForContainerView
              chartData={graphData}
            />
          </div>
        ) : (
          // Show empty data message when there's no data to display
          <NoDataForChart />
        )
      ) : (
        // Show instruction message when not submitted yet
        <div className="cgw-chart-loader-div">
          <BulbOutlined />
          {t("graphInfo")}
        </div>
      )}
    </div>
  );
};

export default ContainerGraphView;
